import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginForm } from '@odo/shared-ui/login/LoginForm';
import { postLoginRedirect } from '@odo/shared-ui/login/postLoginRedirect';
import logo from '../assets/icons/odo.png';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUser } from '../providers/UserProvider';
import { loginUser, verifyUser } from '../services/user';

function LoginErrorAlert({ message, onDismiss }: { message: string; onDismiss: () => void }) {
    return (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <p className="text-sm text-red-700">{message}</p>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="ml-3 flex-shrink-0 inline-flex text-red-400 hover:text-red-600 transition-colors duration-200"
                >
                    <span className="sr-only">Dismiss</span>
                    <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

const Login: React.FC = () => {
    usePageTitle('Log in');
    const { login } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const redirectTo = useMemo(
        () => postLoginRedirect(location.state, location.search),
        [location.state, location.search]
    );

    useEffect(() => {
        const token = localStorage.getItem('odo_login_token');
        if (token) {
            navigate(redirectTo, { replace: true });
        }
    }, [navigate, redirectTo]);

    const handleLogin = async (email: string, password: string) => {
        setIsLoading(true);
        setError('');
        try {
            const loginData = await loginUser(email, password);
            localStorage.setItem('odo_login_token', loginData.token);
            const verifyData = await verifyUser();
            const userData = verifyData.user;
            if (!userData.database.data.isAdmin) {
                localStorage.removeItem('odo_login_token');
                setError('Access denied. Admin privileges required.');
                return;
            }
            login(userData);
            navigate(redirectTo, { replace: true });
        } catch {
            setError('Invalid email or password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LoginForm
            logoSrc={logo}
            title="Admin Dashboard"
            subtitle="Admin access only"
            alert={error && <LoginErrorAlert message={error} onDismiss={() => setError('')} />}
            credentialsInvalid={error !== ''}
            isLoading={isLoading}
            showPasswordReset={false}
            onCredentialsChange={() => setError('')}
            onSubmit={handleLogin}
        />
    );
};

export { Login };
